/**
 * Chat REST calls. Thin wrappers over `api` mirroring mobile
 * `ApiService`'s chat endpoints, parsing with `./types`.
 */
import { api } from '@/lib/api'
import { en } from '@/lib/i18n/en'
import type { Strings } from '@/lib/i18n/types'
import {
  parseConversation,
  parseConversationDetail,
  parseMessage,
  type ChatMessage,
  type Conversation,
} from './types'

export type ChatErrors = Strings['chat']

export interface ConversationPage {
  items: Conversation[]
  totalUnread: number
}

export interface MessagePage {
  items: ChatMessage[]
  nextCursor?: string
}

function pick<T>(data: Record<string, unknown>, camel: string, pascal: string): T | undefined {
  if (data[camel] !== undefined) return data[camel] as T
  if (data[pascal] !== undefined) return data[pascal] as T
  return undefined
}

/** Mobile `loadConversations`: `GET /api/conversations` → items + totalUnread. */
export async function fetchConversations(): Promise<ConversationPage> {
  const res = await api.get('/conversations')
  const data = (res.data ?? {}) as Record<string, unknown>
  const raw = pick<unknown[]>(data, 'items', 'Items') ?? []
  return {
    items: raw.map(parseConversation).filter((c): c is Conversation => c !== null),
    totalUnread: pick<number>(data, 'totalUnread', 'TotalUnread') ?? 0,
  }
}

/**
 * Mobile `ensureConversation`: `GET /api/conversations/{id}` carries the
 * other participant, so the room header renders without a second round-trip.
 */
export async function fetchConversationById(id: string, errors: ChatErrors = en.chat): Promise<Conversation> {
  const res = await api.get(`/conversations/${id}`)
  const conversation = parseConversationDetail(res.data)
  if (!conversation) throw new Error(errors.convUnavailable)
  return conversation
}

/** Mobile `createConversation`: get-or-create a direct conversation. */
export async function createConversation(otherUserId: string, errors: ChatErrors = en.chat): Promise<Conversation> {
  const res = await api.post('/conversations', { otherUserId })
  const conversation = parseConversation(res.data)
  if (!conversation) throw new Error(errors.convStartFailed)
  return conversation
}

/** Mobile `getMessages`: cursor paging, 30 per page like the mobile default. */
export async function fetchMessages(
  conversationId: string,
  cursor?: string,
  limit = 30,
): Promise<MessagePage> {
  const res = await api.get(`/conversations/${conversationId}/messages`, {
    params: { ...(cursor ? { cursor } : {}), limit },
  })
  const data = (res.data ?? {}) as Record<string, unknown>
  const raw = pick<unknown[]>(data, 'items', 'Items') ?? []
  return {
    items: raw.map(parseMessage).filter((m): m is ChatMessage => m !== null),
    nextCursor: pick<string>(data, 'nextCursor', 'NextCursor'),
  }
}

/** Mobile `markConversationAsRead`: confirm read server-side. */
export async function markConversationRead(conversationId: string): Promise<void> {
  await api.post(`/conversations/${conversationId}/read`)
}

/** Backend returns a raw int (mobile reads `data['count']`, which never resolves). */
export async function fetchChatUnreadCount(): Promise<number> {
  const res = await api.get('/conversations/unread-count')
  if (typeof res.data === 'number') return res.data
  const data = res.data as Record<string, unknown> | null
  if (data && typeof data.count === 'number') return data.count
  return 0
}

/** Load failures surface one retryable copy; mutations stay silent like mobile. */
export function mapConversationsError(_err: unknown, errors: ChatErrors = en.chat): string {
  return errors.loadConversations
}
